<template>
    <div class="card">
        <div class="card-header">Personal Information</div>
        <div class="card-body">
            <b-row>
                <b-col>
                    Please fill in all applicable information.  Additional notes or corrections can be added in a notes section at the end of the wizard.
                </b-col>
            </b-row>
            <div role="alert">
                <b-row>
                    <b-col>
                        <div class="text-danger" v-if="renewalApplication.address.filter(a => a.preferred === true).length !== 1">
                            Only one address may be marked as the preferred address.
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <div class="text-danger" v-if="!$v.renewalApplication.address.minLength
                        || !$v.renewalApplication.address.required">
                            At least one valid current address is required.
                        </div>
                    </b-col>
                </b-row>
            </div>

              <b-row>
                <b-col cols="12" lg="4">
                    <BaseTextInput v-model="renewalApplication.lastName"
                                   :v="$v.renewalApplication.lastName"
                                   slug="lastName"
                                   label="Last Name"
                                   setId="applLastName"
                                   :plaintext="opts.nameReadOnly === 'Y' ? true : false"/>
                </b-col>
                <b-col cols="12" lg="4">
                    <BaseTextInput v-model="renewalApplication.firstName"
                                   :v="$v.renewalApplication.firstName"
                                   slug="firstName"
                                   label="First Name"
                                   setId="applFirstName"
                                   :plaintext="opts.nameReadOnly === 'Y' ? true : false"/>
                </b-col>
                <b-col cols="12" lg="4">
                    <BaseTextInput v-model="renewalApplication.middleName"
                                   :v="$v.renewalApplication.middleName"
                                   slug="middleName"
                                   label="Middle Name"
                                   setId="applMiddleName"
                                   :plaintext="opts.nameReadOnly === 'Y' ? true : false"/>
                </b-col>
            </b-row>
            <div v-for="(addr, index) in renewalApplication.address" :key="addr.id">
                <b-row>
                    <b-col cols="3">
                        <BaseSelector v-model="addr.addressType"
                                      :v="$v.renewalApplication.address.$each[index].addressType"
                                      label="Address Type"
                                      slug="addressType"
                                      :options=opts.addressTypeOptions
                                      :setId="'addressType'+index"
                        />
                    </b-col>
                    <b-col cols="3" class="m-auto" >
                        <BaseCheckboxInput v-model="addr.preferred"
                                           :v="$v.renewalApplication.address.$each[index].preferred"
                                           label="Preferred address for contact"
                                           slug="preferred"
                                           :setId="'applPreferred'+index"
                        />
                    </b-col>
                    <b-col cols="6">
                        <BaseTextInput v-model="addr.streetAddress"
                                       :v="$v.renewalApplication.address.$each[index].streetAddress"
                                       slug="streetAddress"
                                       label="Street Address"
                                       :setId="'applStreetAddress'+index"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="4">
                        <BaseTextInput v-model="addr.city"
                                       :v="$v.renewalApplication.address.$each[index].city"
                                       slug="city"
                                       label="City"
                                       :setId="'applCity'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="3" v-if="!renewalApplication.international">
                        <BaseSelector v-model="addr.state"
                                      :v="$v.renewalApplication.address.$each[index].state"
                                      label="State"
                                      slug="state"
                                      :options=opts.stateOptions
                                      :setId="'applState'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="4" v-if="renewalApplication.international">
                        <BaseTextInput v-model="addr.stateProv"
                                       :v="$v.renewalApplication.address.$each[index].stateProv"
                                       slug="stateProv"
                                       label="State/Province"
                                       :setId="'stateProv'+index"
                        />
                    </b-col>
                    <template v-if="!renewalApplication.international">
                        <b-col cols="12" lg="3">
                            <BaseMaskedTextInput v-model="addr.postalCode"
                                                 :v="$v.renewalApplication.address.$each[index].postalCode"
                                                 slug="postalCode"
                                                 label="ZIP"
                                                 :setId="'applZip'+index"
                                                 mask="#####-####"
                            />
                        </b-col>
                        <b-col cols="12" lg="2" class="d-flex align-items-center">
                            <button class="btn btn-secondary" @click="removeAddress(index)">
                                Remove Address
                            </button>
                        </b-col>
                    </template>
                </b-row>
                <b-row v-if="renewalApplication.international">
                    <b-col cols="12" lg="3">
                        <BaseTextInput v-model="addr.postalCode"
                                       :v="$v.renewalApplication.address.$each[index].postalCode"
                                       slug="postalCode"
                                       label="Postal Code"
                                       :setId="'postalCode'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="6">
                        <BaseSelector v-model="addr.country"
                                       :v="$v.renewalApplication.address.$each[index].country"
                                       slug="country"
                                       label="Country"
                                       :setId="'country'+index"
                                       :options=opts.countryOptions
                        />
                    </b-col>
                    <b-col cols="12" lg="2" class="d-flex align-items-center">
                        <button class="btn btn-secondary" @click="removeAddress(index)">
                            Remove Address
                        </button>
                    </b-col>
                </b-row>
            </div>
            <b-row>
                <b-col>
                    <button class="btn btn-secondary" @click="addAddress">
                        Add Another Address
                    </button>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="12" lg="4">
                    <BaseMaskedTextInput v-model="renewalApplication.preferredPhone"
                                   :v="$v.renewalApplication.preferredPhone"
                                   slug="preferredPhone"
                                   label="Preferred Phone Number"
                                   setId="preferredPhone"
                                   mask="(###)###-####"/>
                </b-col>
                <b-col cols="12" lg="8">
                    <BaseTextInput v-model="renewalApplication.emailAddress"
                                   :v="$v.renewalApplication.emailAddress"
                                   slug="emailAddress"
                                   label="Email Address"
                                   setId="applEmailAddress"/>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="12" lg="6">
                    <BaseMaskedTextInput v-model="profile.socialSecurityNbr"
                                         :v="$v"
                                         slug="socialSecurityNumber"
                                         label="Social Security Number"
                                         tip="Required by Minnesota Statute. 270C.72, subdivision 4"
                                         setId="ssn"
                                         :plaintext="true"
                                         mask="###-##-####"/>
                </b-col>
                <b-col cols="12" lg="6">
                    <BaseDateOfBirthInput v-model="profile.dateOfBirth"
                                          :v="$v"
                                          label="Date of Birth"
                                          slug="dateOfBirth"
                                          setId="dob"
                                          :plaintext="true"/>
                </b-col>
            </b-row>
            <div v-if="!isFull()">
                <b-row>
                    <b-col cols="12" lg="12">
                        <BaseRadioInput v-model="otherName"
                                        :v="$v.otherName"
                                        label="Have you ever used another name (including maiden name) under which records may be filed concerning your application, including your education, training or experience?"
                                        slug="otherName"
                                        :options=opts.yesNoOptions
                                        setId="otherRadio"
                        />
                    </b-col>
                </b-row>
                <div v-if="otherName === 'Y'">
                    <b-row v-for="(name, index) in otherNames" :key="name.id">
                        <b-col cols="12" lg="3">
                            <BaseTextInput v-model="name.lastName"
                                           :v="$v.otherNames.$each[index].lastName"
                                           slug="lastName"
                                           label="Last Name"
                                           :key="index"
                                           :setId="'othLastName'+index"
                            />
                        </b-col>
                        <b-col cols="12" lg="3">
                            <BaseTextInput v-model="name.firstName"
                                           :v="$v.otherNames.$each[index].firstName"
                                           slug="firstName"
                                           label="First Name"
                                           :key="index"
                                           :setId="'othFirstName'+index"
                            />
                        </b-col>
                        <b-col cols="12" lg="3">
                            <BaseTextInput v-model="name.middleName"
                                           :v="$v.otherNames.$each[index].middleName"
                                           slug="middleName"
                                           label="Middle Name"
                                           :key="index"
                                           :setId="'othMiddleName'+index"
                            />
                        </b-col>
                        <b-col cols="12" lg="3" class="d-flex align-items-center">
                            <button class="btn btn-secondary" @click="removeName(index)">
                                Remove Name
                            </button>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <button class="btn btn-secondary" @click="addName">
                                Add Another Name
                            </button>
                        </b-col>
                    </b-row>
                </div>
                <b-row>
                    <b-col cols="12" lg="12">
                        <BaseRadioInput v-model="tradeName"
                                        :v="$v.tradeName"
                                        label="Do you currently or have you previously used a nickname or trade name under which records may be filed concerning your application, including your education, training or experience, or which by which you are commonly known for your work?"
                                        slug="tradeName"
                                        :options=opts.yesNoOptions
                                        setId="tradeRadio"
                        />
                    </b-col>
                </b-row>
                <div v-if="tradeName === 'Y'">
                    <b-row v-for="(name, index) in tradeNames" :key="name.id">
                        <b-col cols="12" lg="6">
                            <BaseTextInput v-model="name.name"
                                           :v="$v.tradeNames.$each[index].name"
                                           slug="name"
                                           label="Name"
                                           :key="index"
                                           :setId="'tradeName'+index"
                            />
                        </b-col>
                        <b-col cols="12" lg="3" class="d-flex align-items-center">
                            <button class="btn btn-secondary" @click="removeTradeName(index)">
                                Remove Name
                            </button>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <button class="btn btn-secondary" @click="addTradeName">
                                Add Another Name
                            </button>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </div>

    </div>

</template>
<script>
import { required, requiredIf, email, maxLength, minLength } from 'vuelidate/lib/validators'
import { commonOptions } from '@/mixins/commonOptions.js'


export default {
    data: () => {
        return {
            opts: {
                nameReadOnly: 'N',
                addressTypeOptions: [
                    { value: 'H', text: 'Home' },
                    { value: 'E', text: 'Employer' },
                ],
            },
            otherName: null,
            tradeName: null,
            otherNames: [],
            tradeNames: []
        }
    },
    
    props: {
        renewalApplication: {
            type: Object,
            default () {
                return {
                    address: [{
                        id:null,
                        streetAddress: null,
                        city: null,
                        state: null,
                        stateProv: null,
                        postalCode: null,
                        country: null,
                        addressType: 'H',
                        preferred: true,
                        applicationId: null
                    }]
                }
            }
        },
        profile: [Object]
    },

    created(){
        if (this.renewalApplication.address == null) {
            this.renewalApplication.address = [{
                id:null,
                streetAddress: null,
                city: null,
                state: null,
                stateProv: null,
                postalCode: null,
                country: null,
                addressType: 'H',
                preferred: true,
                applicationId: this.renewalApplication.id
            }]
        }
    },

    mixins: [
        commonOptions
    ],
    
    validations() {
        return {
            renewalApplication: {
                lastName: { required },
                firstName: { required },
                middleName: { required: false },
                address: {
                    required: true,
                    minLength: minLength(1),
                    $each: {
                        streetAddress: { required },
                        city: { required },
                        state: { required: requiredIf(function() { return !this.renewalApplication.international }) },
                        stateProv: { required: requiredIf(function() { return this.renewalApplication.international }) },
                        postalCode: { required: requiredIf(function() { return !this.renewalApplication.international }),
                            maxLength: maxLength(10),
                            minLength: this.renewalApplication.international ? minLength(3) : minLength(5) },
                        country: { required: requiredIf(function() { return this.renewalApplication.international }),
                            maxLength: maxLength(30) },
                        addressType: { required },
                        preferred: { checked: value => this.renewalApplication.address.filter(a => a.preferred === true).length === 1 },
                        id: { required: false },
                        applicationId: { required: false },
                    }
                },
                preferredPhone: { required },
                emailAddress: { required, email },
            },
            otherNames: {
                required: requiredIf(function() { return this.otherName === 'Y' }),
                $each: {
                    firstName: { required: requiredIf(function() { return this.otherName === 'Y' }) },
                    middleName: { required: false },
                    lastName: { required: requiredIf(function() { return this.otherName === 'Y' }) }
                }
            },
            tradeNames: {
                required: requiredIf(function() { return this.tradeName === 'Y' }),
                $each: {
                    name: { required: requiredIf(function() { return this.tradeName === 'Y' }) },
                }
            },
            otherName: { required },
            tradeName: { required },
            form: [ 'renewalApplication', "otherName", "tradeName" ]
        }            
    },
    methods: {
        validate() {
            this.$v.form.$touch();
            var isValid = !this.$v.form.$invalid
            this.renewalApplication.otherNames = this.otherName === 'Y' ? this.otherNames : []
            this.renewalApplication.tradeNames = this.tradeName === 'Y' ? this.tradeNames : []
            this.$emit('on-validate', this.$data.renewalApplication, isValid)
            return isValid
        },
        addAddress: function () {
            this.renewalApplication.address.push({
                id:null,
                streetAddress: null,
                city: null,
                state: null,
                stateProv: null,
                postalCode: null,
                country: null,
                addressType: 'H',
                preferred: false,
                applicationId: null
            });
        },
        removeAddress: function (i) {
            this.$delete(this.renewalApplication.address, i)
        },
        isFull() {
            return this.renewalApplication.licenseSubCategory === 'FULL'
        },
        addName: function () {
            this.otherNames.push({
                lastName: null ,
                firstName: null ,
                middleName: null
            });
        },
        removeName: function (i) {
            this.$delete(this.otherNames, i)
        },
        addTradeName: function () {
            this.tradeNames.push({
                name: null ,
            });
        },
        removeTradeName: function (i) {
            this.$delete(this.tradeNames, i)
        },
    },
    watch: {
        profile() {
            this.opts.nameReadOnly = 'N'

            if (this.profile.firstName && this.profile.lastName) {
                this.renewalApplication.firstName = this.profile.firstName
                this.renewalApplication.lastName = this.profile.lastName
                this.renewalApplication.middleName = this.profile.middleName
                this.opts.nameReadOnly = 'Y'
            }
        },
        renewalApplication() {
            if (this.renewalApplication.tradeNames == null || this.renewalApplication.tradeNames.length === 0) {
                this.tradeNames =  [{
                    applicationId: null,
                    id: null,
                    name: null
                }]
                this.tradeName = 'N'
            } else {
                this.tradeNames = []
                this.renewalApplication.tradeNames.forEach(o => {
                    this.tradeNames.push(o)
                })
                this.tradeName = 'Y'
            }

            if (this.renewalApplication.otherNames == null || this.renewalApplication.otherNames.length === 0) {
                this.otherNames =  [{
                    applicationId: null,
                    id: null,
                    firstName: null,
                    middleName: null,
                    lastName: null
                }]
                this.otherName = 'N'
            } else {
                this.otherNames = []
                this.renewalApplication.otherNames.forEach(o => {
                    this.otherNames.push(o)
                })
                this.otherName = 'Y'
            }
        }
    }
}
</script>